import { isMatch } from 'date-fns';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnDestroy,
  inject,
  input,
  output,
} from '@angular/core';
import { Column, DataDisplayType } from '@navstation-portal/reporting/data-model/ui';
import { IconName, IconNames } from '@navstation-portal/shared/data-model/ui';
import { IconComponent } from '@navstation-portal/shared/ui/components';
import { DecimalHoursPipe } from '@navstation-portal/shared/util/formatting';
import { TableModule, TableRowSelectEvent } from 'primeng/table';
import { ReportBadgeComponent } from '../report-badge/report-badge.component';

interface IconData {
  name: IconName;
  class: string;
}

const STATUS_ICON_DATA_MAP: Record<string, IconData> = {
  completed: { name: IconNames.Circle, class: 'text-green-500' },
  started: {
    name: IconNames.CircleHalfStroke,
    class: 'text-orange-400',
  },
  draft: {
    name: IconNames.CircleHalfStroke,
    class: 'text-orange-400',
  },
} as const;

@Component({
  selector: 'nsp-table',
  templateUrl: './table.component.html',
  styleUrl: './table.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TableModule, DatePipe, DecimalPipe, DecimalHoursPipe, IconComponent, ReportBadgeComponent],
})
export class TableComponent<T> implements AfterViewChecked, OnDestroy {
  readonly IconNames = IconNames;

  items = input.required<T[]>();
  columns = input.required<Column[]>();
  virtualScroll = input(false);
  DataType = DataDisplayType;

  selectChanged = output<TableRowSelectEvent>();
  showScrollTop = false;

  private readonly hostElement = inject(ElementRef<HTMLElement>);
  private scrollContainer: HTMLElement | null = null;
  private removeScrollListener: (() => void) | null = null;

  ngAfterViewChecked(): void {
    if (this.items().length === 0) {
      this.cleanupScrollListener();
      this.scrollContainer = null;
      this.showScrollTop = false;
      return;
    }

    if (this.scrollContainer && !this.scrollContainer.isConnected) {
      this.cleanupScrollListener();
      this.scrollContainer = null;
    }

    if (!this.scrollContainer) {
      this.bindScrollContainer();
    }
  }

  ngOnDestroy(): void {
    this.cleanupScrollListener();
  }

  isDate(dateStr: string) {
    if (!dateStr) return false;
    if (typeof dateStr !== 'string') return false;

    return isMatch(dateStr, "yyyy-MM-dd'T'HH:mm:ss") || isMatch(dateStr, "yyyy-MM-dd'T'HH:mm:ss.SSSX");
  }

  convertToDateObj(dateStr: string | number | Date) {
    return new Date(dateStr);
  }

  getDataTypeString(value: DataDisplayType): string {
    return value.toString();
  }

  onRowSelect(event: TableRowSelectEvent) {
    this.selectChanged.emit(event);
  }

  getStatusIconData(status?: string): IconData | null {
    if (!status) return null;

    return STATUS_ICON_DATA_MAP[status] ?? null;
  }

  scrollToTop() {
    const scrollElement = this.scrollContainer ?? this.getScrollContainer();
    if (!scrollElement) return;

    scrollElement.scrollTo({ top: 0, behavior: 'smooth' });
  }

  private bindScrollContainer() {
    const scrollElement = this.getScrollContainer();
    if (!scrollElement) return;

    this.cleanupScrollListener();
    this.scrollContainer = scrollElement;
    this.showScrollTop = scrollElement.scrollTop > 0;

    const onScroll = () => {
      this.showScrollTop = scrollElement.scrollTop > 0;
    };

    scrollElement.addEventListener('scroll', onScroll, { passive: true });
    this.removeScrollListener = () => {
      scrollElement.removeEventListener('scroll', onScroll);
    };
  }

  private cleanupScrollListener() {
    if (!this.removeScrollListener) return;

    this.removeScrollListener();
    this.removeScrollListener = null;
  }

  private getScrollContainer(): HTMLElement | null {
    return (
      this.hostElement.nativeElement.querySelector('.p-virtualscroller') ??
      this.hostElement.nativeElement.querySelector('.p-datatable-table-container')
    );
  }
}
